import type { QuickBooksControlCenterList } from '.';

// At most this many pages of one list (60 x 200 = 12,000 rows); a longer
// list is shown truncated and says so (money-lanes.tsx TruncatedNote /
// PartialTotalsNote). Production on 16 Oct 2026: 1,365 expense groups and
// 4,564 payout rows; the old bound of 25 pages (5,000 rows) was about to cut
// Payouts short.
export const MAX_CONTROL_CENTER_LIST_PAGES = 60;

// Pages read at the same time. Reading 23 pages one after another made the
// Payouts tab wait for every round trip in turn.
export const CONTROL_CENTER_LIST_PAGE_CONCURRENCY = 4;

type PageAnswer = { data?: unknown; error?: unknown };

// True when a list read every page the server said it has. A list whose
// read stopped early (the page bound, a failed page, or a page from another
// revision) keeps the server's total, so `rows.length < total`; its sums are
// partial and the screen must say so.
export const controlCenterListComplete = (
  list?: QuickBooksControlCenterList | null
) => {
  const total = list?.pagination?.total;
  return typeof total !== 'number' || (list?.rows?.length ?? 0) >= total;
};

// The rest of a list after its first page, read page by page at the same
// page size (a few pages at a time). A page that fails, comes back empty or
// short, or was answered from another revision of the projection (another
// asOf or total) ends the read: the rows returned are always one revision,
// in order with no gap, and `pagination.total` keeps the server's total so
// the screen says how many it could not show.
export const readRemainingControlCenterPages = async (
  first: QuickBooksControlCenterList,
  read: (offset: number) => PageAnswer | PromiseLike<PageAnswer>,
  maxPages = MAX_CONTROL_CENTER_LIST_PAGES
): Promise<QuickBooksControlCenterList> => {
  const total = first.pagination?.total;
  if (typeof total !== 'number' || (first.pagination?.offset ?? 0) !== 0) {
    return first;
  }
  const rows = [...(first.rows || [])];
  const pageSize = rows.length;
  if (pageSize === 0 || rows.length >= total) {
    return {
      ...first,
      rows,
      pagination: { total, offset: 0, limit: rows.length },
    };
  }
  const offsets: number[] = [];
  for (
    let offset = pageSize;
    offset < total && offsets.length + 1 < maxPages;
    offset += pageSize
  ) {
    offsets.push(offset);
  }
  const usable = (offset: number, page: PageAnswer) => {
    const data = page.data as QuickBooksControlCenterList | undefined;
    const count = data?.rows?.length ?? 0;
    return Boolean(
      !page.error &&
      data &&
      count > 0 &&
      data.asOf === first.asOf &&
      data.pagination?.total === total &&
      // Every page but the last is full, so the next offset has no gap.
      (count === pageSize || offset + count >= total)
    );
  };
  let stopped = false;
  for (
    let start = 0;
    start < offsets.length && !stopped;
    start += CONTROL_CENTER_LIST_PAGE_CONCURRENCY
  ) {
    const batch = offsets.slice(
      start,
      start + CONTROL_CENTER_LIST_PAGE_CONCURRENCY
    );
    const answers = await Promise.all(
      batch.map(async (offset) => {
        try {
          return await read(offset);
        } catch (error) {
          return { error };
        }
      })
    );
    for (let index = 0; index < answers.length; index += 1) {
      const answer = answers[index];
      if (!usable(batch[index], answer)) {
        stopped = true;
        break;
      }
      rows.push(...(answer.data as QuickBooksControlCenterList).rows);
    }
  }
  return {
    ...first,
    rows,
    pagination: { total, offset: 0, limit: rows.length },
  };
};
